<script lang="ts">
	import { onMount, type Component } from 'svelte'
	import type { ContextMenuItem, ContextMenuProps } from '@repo/commons/models'
	import TableCell from './TableCell.svelte'
	import { rowActions } from '@repo/commons/components'

	interface Props {
		rows?: any[]
		enableRowSelect?: boolean
		selectedRowsSet?: Set<any>
		columns: string[]
		columnsToSplit?: Record<string, string>
		columnStyles?: Record<string, string>
		editableColumns: string[]
		jsonColumns?: string[]
		locale?: string
		columnComponents?: Record<string, Component>
		shouldHighlightCurlyBraces?: boolean
		colorRowOnHover?: boolean
		handleContextMenu: (options: ContextMenuProps | null) => void
		handleCheckboxChange: (checked: boolean, id: number) => void
		onRowDbClick: ((row: any) => void) | null
		onsubmit: ((row: Record<string, string>) => void) | null
		onActionHandler: ((action: rowActions, id: string) => void) | null
		onRowClicked: ((row: any) => void) | null
		contextMenuOptions: ((row: any, index: number) => ContextMenuItem[]) | null
	}

	let {
		rows = [],
		enableRowSelect = true,
		selectedRowsSet = new Set<any>(),
		columns,
		columnsToSplit = {},
		columnStyles = {},
		editableColumns = [],
		jsonColumns = [],
		locale = 'de',
		columnComponents = {},
		shouldHighlightCurlyBraces = false,
		colorRowOnHover = false,
		handleContextMenu,
		handleCheckboxChange,
		onRowDbClick = null,
		onsubmit = null,
		onActionHandler = null,
		onRowClicked = null,
		contextMenuOptions = null
	}: Props = $props()
	let clickTimeout: NodeJS.Timeout | null = $state(null)
	let preventClick = $state(false)
	let focusedRowId: string | null = $state<string | null>(null)

	const handleRowFocus = (rowId: string) => {
		focusedRowId = rowId
	}
	const handleRowBlur = () => {
		focusedRowId = null
		preventClick = false
	}

	const handleStartEditing = () => {
		preventClick = true
	}

	const onRowClick = (row: any) => {
		if (clickTimeout) {
			clearTimeout(clickTimeout)
		}
		clickTimeout = setTimeout(() => {
			if (!preventClick) {
				onRowClicked?.(row)
			}
		}, 300)
	}
	const openContextMenu = (event: MouseEvent, row: any, index: number) => {
		event.preventDefault()

		const rowElement = event.currentTarget as HTMLElement
		const rowRect = rowElement.getBoundingClientRect()
		const tableRect = rowElement.closest('table')?.getBoundingClientRect()

		const scrollX = window.scrollX
		const scrollY = window.scrollY

		/*
		Detect if table is inside a `.sidesheet` which is its usage in smb for the UseCaseForm.svelte.
		TODO: This is supposed to be a temporary fix which necessarily creates a dependency of this 
		component to its particular usage there!
		*/
		//const isInsideSideSheet = !!rowElement.closest('.sidesheet')

		const yOffset = /*isInsideSideSheet ? 50 :*/ rowRect.height
		const contextMenuProps = {
			x: event.clientX + scrollX - (tableRect?.left ?? 0),
			y: event.clientY + scrollY - (tableRect?.top ?? 0) + yOffset,
			items: contextMenuOptions?.(row, index) ?? []
		}
		handleContextMenu(contextMenuProps)
	}
	const closeContextMenu = () => {
		handleContextMenu(null)
	}

	onMount(() => {
		document.addEventListener('click', closeContextMenu)
	})
</script>

{#each rows as row, index (row.id)}
	<div
		role="button"
		tabindex="0"
		class="row"
		class:selected={selectedRowsSet.has(row.id)}
		class:row-on-hover={colorRowOnHover}
		class:editing={focusedRowId === row.id}
		onclick={() => onRowClick(row)}
		onkeydown={(e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault()
				onRowClick(row)
			}
		}}
		oncontextmenu={(e) => openContextMenu(e, row, index)}
	>
		<TableCell
			{enableRowSelect}
			{selectedRowsSet}
			{row}
			{columns}
			{columnsToSplit}
			{columnStyles}
			{editableColumns}
			{jsonColumns}
			{locale}
			{focusedRowId}
			{columnComponents}
			{shouldHighlightCurlyBraces}
			{handleCheckboxChange}
			{onRowDbClick}
			onHandleRowFocus={handleRowFocus}
			onHandleRowBlur={handleRowBlur}
			{onsubmit}
			{onActionHandler}
			onHandleStartEditing={handleStartEditing}
		/>
	</div>
{/each}

<style lang="postcss">
	.row {
		display: contents;
	}
	div.selected {
		@apply bg-table-hover;
	}

	div.row-on-hover:hover {
		@apply bg-table-hover cursor-pointer;
	}

	div.editing {
		background-color: #007acc0d;
	}
</style>
